import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { mobileUrl } from '@orgus/protocol';
import { useGateView } from '../auth/AuthGate.tsx';
import type { GateView } from '../auth/AuthGate.tsx';

/** A phone: a touch screen whose short side is under 768 px, 48em (tablets keep the console). */
export const PHONE_QUERY = '(pointer: coarse) and (max-width: 47.98em), (pointer: coarse) and (max-height: 47.98em) and (orientation: landscape)';

const KEEP_CONSOLE = 'orgus.console';

/**
 * Whether this tab asked for the full console: a link from the phone layout carries
 * ?console=1, and the choice lasts until the tab closes.
 */
export function wantsConsole(search: string, stored: string | null): boolean {
  return stored === '1' || new URLSearchParams(search).get('console') === '1';
}

/** The phone layout's address for a console address, keeping demo data on. */
export function phoneTarget(pathname: string, search: string): string {
  const to = mobileUrl(`${pathname}${search}`);
  const mock = new URLSearchParams(search).get('mock');
  if (!mock || to.includes('mock=')) return to;
  return `${to}${to.includes('?') ? '&' : '?'}mock=${encodeURIComponent(mock)}`;
}

/**
 * Whether to move this page to the phone layout. Only once the auth gate lets the app through:
 * the phone layout has no sign-in screen, so a signed-out phone stays on the console's sign-in
 * form (and the splash while the hub has not answered), then moves once signed in.
 */
export function shouldSwitch(view: GateView, phone: boolean, keepConsole: boolean): boolean {
  return view === 'app' && phone && !keepConsole;
}

function readStored(): string | null {
  try {
    return sessionStorage.getItem(KEEP_CONSOLE);
  } catch {
    return null;
  }
}

/**
 * On a phone the console opens in the phone layout (/m) by itself once signed in: no banner, no extra tap.
 * A desktop window made narrow keeps the console. The phone layout's "Open the full
 * console" link adds ?console=1, which keeps this tab on the console.
 */
export function PhoneLayoutSwitch() {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const view = useGateView((s) => s.view);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const keep = wantsConsole(search, readStored());
    if (keep) {
      try {
        sessionStorage.setItem(KEEP_CONSOLE, '1');
      } catch {
        // Storage blocked: the query parameter still keeps this page on the console.
      }
      return;
    }
    if (shouldSwitch(view, window.matchMedia(PHONE_QUERY).matches, keep)) navigate(phoneTarget(pathname, search), { replace: true });
  }, [navigate, pathname, search, view]);
  return null;
}

